import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";

interface OnboardingModalProps {
  isOpen: boolean;
  userId: string;
  onClose: () => void;
}

const AGE_GROUPS = [
  { value: "3-4", label: "3 – 4 years", emoji: "🧸" },
  { value: "5-6", label: "5 – 6 years", emoji: "🎨" },
  { value: "7-8", label: "7 – 8 years", emoji: "🚀" },
];

const OnboardingModal = ({ isOpen, userId, onClose }: OnboardingModalProps) => {
  const [ageGroup, setAgeGroup] = useState<string>("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (skip = false) => {
    setSaving(true);
    try {
      const updates: Record<string, any> = {};
      if (!skip) {
        if (ageGroup) updates.child_age_group = ageGroup;
        if (phone.trim()) updates.phone_number = phone.trim();
      }
      // Mark that onboarding has been seen by setting at least the age group if chosen,
      // otherwise just close. We don't force any field.
      if (Object.keys(updates).length > 0) {
        const { error } = await supabase.from("profiles").update(updates).eq("id", userId);
        if (error) {
          console.error("Onboarding save error:", error);
          toast.error("Could not save your preferences");
          setSaving(false);
          return;
        }
        toast.success("All set! Have fun exploring 🎉");
      }
      // Remember dismissal locally so it doesn't reappear on every visit
      try {
        localStorage.setItem(`tm_onboarded_${userId}`, "1");
      } catch {}
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => save(true)}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-cyan-50 via-white to-pink-50 rounded-2xl border border-cyan-200">
        <DialogHeader>
          <DialogTitle className="text-2xl font-fredoka font-bold text-center flex items-center justify-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-500" />
            Welcome aboard!
          </DialogTitle>
          <DialogDescription className="text-center">
            Tell us a little about your little one to personalize the fun.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Child's age</Label>
            <div className="grid grid-cols-3 gap-2">
              {AGE_GROUPS.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => setAgeGroup(g.value)}
                  className={`p-3 rounded-xl border-2 transition-all text-center ${
                    ageGroup === g.value
                      ? "border-amber-400 bg-amber-50 shadow-md scale-105"
                      : "border-gray-200 bg-white hover:border-amber-200"
                  }`}
                >
                  <div className="text-2xl">{g.emoji}</div>
                  <div className="text-xs font-semibold mt-1">{g.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ob-phone" className="text-sm font-semibold">
              Mobile number <span className="text-gray-400 font-normal">(optional)</span>
            </Label>
            <Input
              id="ob-phone"
              type="tel"
              placeholder="e.g. +91 98765 43210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              variant="ghost"
              className="flex-1"
              onClick={() => save(true)}
              disabled={saving}
            >
              Skip for now
            </Button>
            <Button
              className="flex-1 bg-amber-400 hover:bg-amber-500 text-gray-900 font-semibold"
              onClick={() => save(false)}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Continue"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
